import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FaMusic, FaGamepad, FaChartLine, FaHeadphones } from 'react-icons/fa';
import gsap from 'gsap';

const Features: React.FC = () => {
  // Features data
  const features = [
    {
      icon: <FaMusic className="w-10 h-10 text-yellow-500" />,
      title: "Learn Spanish with Real Music",
      description: "Master Spanish through popular songs you already love. Lyrics become your learning material.",
      color: "yellow",
      delay: 0
    },
    {
      icon: <FaGamepad className="w-10 h-10 text-purple-500" />,
      title: "Practice Mode for Exercises",
      description: "Interactive exercises based on song lyrics help reinforce vocabulary and grammar.",
      color: "purple",
      delay: 0.1
    },
    {
      icon: <FaChartLine className="w-10 h-10 text-blue-500" />,
      title: "Track Your Progress",
      description: "See your improvement over time with detailed analytics and personalized insights.",
      color: "blue",
      delay: 0.2
    },
    {
      icon: <FaHeadphones className="w-10 h-10 text-red-500" />,
      title: "Fun, Game-Like Quizzes",
      description: "Test your knowledge with engaging quizzes based on the songs you're learning from.",
      color: "red",
      delay: 0.3
    }
  ];

  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });
  const controls = useAnimation();

  // Animate cards when section comes into view
  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  // Set up parallax effect
  useEffect(() => {
    if (!sectionRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const section = sectionRef.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const moveX = (clientX - centerX) / 50;
      const moveY = (clientY - centerY) / 50;

      // Apply parallax to each card
      cardsRef.current.forEach((card, index) => {
        if (!card) return;
        
        // Different intensity for each card to create depth
        const intensity = 1 - (index * 0.2);
        
        gsap.to(card, {
          x: moveX * intensity,
          y: moveY * intensity,
          rotateX: -moveY * 0.2 * intensity,
          rotateY: moveX * 0.2 * intensity,
          duration: 0.5,
          ease: "power2.out"
        });
      });
    };

    // Reset positions when mouse leaves
    const handleMouseLeave = () => {
      cardsRef.current.forEach((card) => {
        if (!card) return;
        
        gsap.to(card, {
          x: 0,
          y: 0,
          rotateX: 0,
          rotateY: 0,
          duration: 0.5,
          ease: "power2.out"
        });
      });
    };

    const sectionElement = sectionRef.current;
    sectionElement.addEventListener('mousemove', handleMouseMove);
    sectionElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      sectionElement.removeEventListener('mousemove', handleMouseMove);
      sectionElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  // Function to get gradient based on color
  const getGradient = (color: string) => {
    switch (color) {
      case 'yellow':
        return 'from-yellow-500/20 to-yellow-700/20 hover:from-yellow-500/30 hover:to-yellow-700/30 border-yellow-500/30 hover:border-yellow-400/50';
      case 'purple':
        return 'from-purple-500/20 to-purple-700/20 hover:from-purple-500/30 hover:to-purple-700/30 border-purple-500/30 hover:border-purple-400/50';
      case 'blue':
        return 'from-blue-500/20 to-blue-700/20 hover:from-blue-500/30 hover:to-blue-700/30 border-blue-500/30 hover:border-blue-400/50';
      case 'red':
        return 'from-red-500/20 to-red-700/20 hover:from-red-500/30 hover:to-red-700/30 border-red-500/30 hover:border-red-400/50';
      default:
        return 'from-gray-500/20 to-gray-700/20 hover:from-gray-500/30 hover:to-gray-700/30 border-gray-500/30 hover:border-gray-400/50';
    }
  };

  return (
    <section ref={sectionRef} className="py-24 px-6 relative overflow-hidden" id="features">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 -z-10">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-500/10 rounded-full filter blur-[100px] animate-blob"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full filter blur-[100px] animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/10 rounded-full filter blur-[100px] animate-blob animation-delay-4000"></div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-4 relative inline-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="shimmer-text">Why Choose Melodía Spanish</span>
            <motion.span 
              className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-yellow-400 to-transparent rounded-full"
              initial={{ width: 0 }}
              whileInView={{ width: '100%' }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.5 }}
            ></motion.span>
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Our unique approach combines the joy of music with effective language learning techniques
          </motion.p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              ref={el => cardsRef.current[index] = el}
              className={`feature-card relative bg-gradient-to-br ${getGradient(feature.color)} backdrop-blur-xl rounded-3xl p-8 border transition-all duration-500 transform perspective-1000 hover:shadow-2xl`}
              variants={cardVariants}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
            >
              {/* Glass Reflection Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl pointer-events-none"></div>
              
              {/* Icon with Animation */}
              <motion.div 
                className="mb-6 relative"
                whileHover={{ 
                  scale: 1.1,
                  rotate: [0, 5, -5, 0],
                  transition: { duration: 0.5, repeat: Infinity, repeatType: "reverse" }
                }}
              >
                {/* Icon Background Glow */}
                <div className={`absolute inset-0 rounded-full ${
                  feature.color === 'yellow' ? 'bg-yellow-500/20' :
                  feature.color === 'purple' ? 'bg-purple-500/20' :
                  feature.color === 'blue' ? 'bg-blue-500/20' :
                  'bg-red-500/20'
                } filter blur-xl transform scale-150 opacity-70`}></div>
                
                {/* Icon */}
                {feature.icon}
                
                {/* Animated Particles */}
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 rounded-full ${
                      feature.color === 'yellow' ? 'bg-yellow-400' :
                      feature.color === 'purple' ? 'bg-purple-400' :
                      feature.color === 'blue' ? 'bg-blue-400' :
                      'bg-red-400'
                    }`}
                    initial={{ 
                      x: 0, 
                      y: 0, 
                      opacity: 0,
                      scale: 0
                    }}
                    animate={{ 
                      x: [0, (Math.random() - 0.5) * 50],
                      y: [0, (Math.random() - 0.5) * 50],
                      opacity: [0, 1, 0],
                      scale: [0, Math.random() + 0.5, 0]
                    }}
                    transition={{ 
                      duration: 2 + Math.random() * 2,
                      repeat: Infinity,
                      repeatType: "loop",
                      delay: Math.random() * 2
                    }}
                  />
                ))}
              </motion.div>
              
              {/* Content */}
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-gray-300">{feature.description}</p>
              
              {/* Hover Indicator */}
              <motion.div
                className="absolute bottom-4 right-4 opacity-0 text-xs text-gray-400"
                whileHover={{ opacity: 1 }}
              >
                Hover for effect
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Floating Musical Notes */}
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-gray-600/10 pointer-events-none"
            initial={{ 
              x: Math.random() * 100 + '%', 
              y: Math.random() * 100 + '%',
              rotate: Math.random() * 360,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{ 
              y: [null, '-20%'],
              rotate: [null, Math.random() * 360 + 180],
              opacity: [0.1, 0]
            }}
            transition={{ 
              duration: Math.random() * 10 + 15, 
              repeat: Infinity,
              ease: "linear"
            }}
            style={{ 
              fontSize: `${Math.random() * 3 + 2}rem`,
              filter: 'blur(1px)'
            }}
          >
            {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][Math.floor(Math.random() * 7)]}
          </motion.div>
        ))}
      </div>
      
      {/* Custom Styles */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
        
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        
        .feature-card:hover::before {
          content: '';
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          background: linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0));
          border-radius: 3xl;
          z-index: -1;
          animation: rotate 2s linear infinite;
        }
        
        @keyframes rotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </section>
  );
};

export default Features;